import { Component, EventEmitter, inject, OnDestroy, Output } from '@angular/core';
import { WelcomeBoxComponent } from '../welcome-box/welcome-box.component';
import { CommonModule } from '@angular/common';
import { GlobalService } from '../../services/common/global.service';
import { AvatarUtils } from '../../services/utils/avatar-utils';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SimplebarAngularModule } from 'simplebar-angular';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { PortalTopNavComponent, PortalTopNavTab } from '../../../doctor-portal/components/common/portal-top-nav/portal-top-nav.component';
import { ToasterMessageService } from '../../services/topbar-toaster/toaster-message.service';

@Component({
  selector: 'app-top-bar-2',
  imports: [WelcomeBoxComponent, CommonModule, SimplebarAngularModule, NgbTooltipModule, PortalTopNavComponent],
  templateUrl: './top-bar-2.component.html',
  styleUrl: './top-bar-2.component.css',
})
export class TopBar2Component implements OnDestroy {
  private readonly dashboardStateKey = 'doctorDashboardV2State';
  @Output() settingsButtonClicked = new EventEmitter();
  @Output() openOnboardingOverview = new EventEmitter<{
    title: string;
    count: string;
    tab: string;
  }>();
  userSettings!: any;
  avatar: SafeHtml | null = null;
  isAvatar: boolean = false;

  private globalService = inject(GlobalService);
  private sanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private messageService = inject(ToasterMessageService);

  activeTab: PortalTopNavTab = 'dashboard';
  hasPatientOutcomesPermission = false;
  private routerSub: Subscription;
  private messageSub: Subscription;

  // Toast state — same fields/behavior as main-layout.component.ts's toast,
  // which only renders for non-doctor roles. The doctor portal's header
  // (this component) never had it, so errroMessage()/successMessage() etc.
  // silently had nowhere to show up here.
  currentMessage: string = '';
  currentClass: string = '';
  messageTextClass: string = '';
  isVisible: boolean = false;
  icon: string = '';
  path: string = '';
  reload: boolean = false;

  constructor() {
    this.routerSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.activeTab = this.resolveActiveTab(event.urlAfterRedirects);
      }
    });
    this.activeTab = this.resolveActiveTab(this.router.url);

    this.messageSub = this.messageService.currentMessage.subscribe(
      ({ message, class: className, textClass, icon, time, isVisible, path, reload }) => {
        this.currentMessage = message;
        this.currentClass = className;
        this.messageTextClass = textClass;
        this.isVisible = isVisible;
        this.icon = icon;
        this.path = path;
        this.reload = reload;
        if (this.isVisible && time !== 0) {
          setTimeout(() => {
            this.isVisible = false;
          }, time);
        }
      },
    );
  }

  handlerGo(): void {
    if (this.path) {
      if (this.router.url === this.path) {
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate([this.path]);
        });
      } else {
        this.router.navigate([this.path]);
      }
      this.closeToast();
    }
  }

  handlerReload(): void {
    window.location.reload();
  }

  closeToast(): void {
    this.isVisible = false;
  }

  private hasScreenPermission(screenName: string): boolean {
    const userPermissions = this.userSettings?.userPermissions;
    if (!userPermissions) {
      return false;
    }
    return userPermissions.some((permission: any) =>
      permission.screens?.some(
        (screen: any) => screen.screen_name === screenName && screen.is_assigned,
      ),
    );
  }

  private resolveActiveTab(url: string): PortalTopNavTab {
    if (url.startsWith('/dashboard/doctor/patient-outcomes')) {
      return 'patient-outcomes';
    }
    if (url.includes('/doctor-dashboard/referrals')) {
      return 'referrals';
    }
    return 'dashboard';
  }

  goToDashboard(): void {
    this.router.navigate(['/dashboard/doctor/doctor-dashboard']);
  }

  goToPatientOutcomes(): void {
    this.router.navigate(['/dashboard/doctor/patient-outcomes']);
  }

  goToReferrals(): void {
    this.router.navigate(['/dashboard/doctor/doctor-dashboard/referrals']);
  }

  goToCompliance(): void {
    this.router.navigate(['/dashboard/doctor/doctor-dashboard'], {
      queryParams: { focus: 'compliance' },
    });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.messageSub?.unsubscribe();
  }

  ngOnInit(): void {
    this.userSettings = JSON.parse(this.globalService.getUserSettings());
    this.hasPatientOutcomesPermission = this.hasScreenPermission('Patients Outcome Dashboard');
    let thumbnail = this.userSettings.userInfo.imagePath;

    if (thumbnail == null || thumbnail == '' || thumbnail == 'undefined') {
      const avatarHtml = AvatarUtils.getInitialsWithColorForBar(
        this.userSettings.userInfo.first_name,
        this.userSettings.userInfo.last_name,
        false,
      );
      this.avatar = this.sanitizer.bypassSecurityTrustHtml(avatarHtml);
      this.isAvatar = true;
    }
  }

  getInitials(firstName: string, lastName: string): string {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last;
  }

  

  openHomeHealthConsent() {
    console.log('here')
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/dashboard/patientonboarding/other-programs']),
    );
    window.open(url, '_blank');
  }

  OpenOnboardingOverview() {
    const payload = {
      title: 'Document Center',
      count: 'Signed-Off',
      tab: 'documentcenter',
    };

    this.saveDashboardState('onboarding', payload);
    this.openOnboardingOverview.emit(payload);
  }

  private saveDashboardState(screen: string, payload: any): void {
    const existingState = this.loadDashboardState();
    const nextState = {
      currentScreen: screen,
      screens: {
        ...(existingState?.screens || {}),
        [screen]: payload,
      },
      filter: existingState?.filter || {},
      savedAt: new Date().toISOString(),
    };

    localStorage.setItem(this.dashboardStateKey, JSON.stringify(nextState));
  }

  private loadDashboardState(): any {
    try {
      const raw = localStorage.getItem(this.dashboardStateKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  settingMenu() {
    this.settingsButtonClicked.emit();
  }
}
